import { Box, Button, Modal, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import PageToaster from "../toaster/PageToaster";

interface SaleForCancel {
  sale_id: number;
  sale_number: number;
  total: string | number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCanceled: () => void;
  sale: SaleForCancel | null;
}

const SaleCancelModal: React.FC<Props> = ({ open, onClose, onCanceled, sale }) => {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const containerId = "toaster-sale-cancel-modal";
  const { autoSuccess, autoError } = usePageToast(containerId, true);
  const handleHttpError = makeToasterErrorHandler(autoError);

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const handleSubmit = async () => {
    if (!sale) return;
    if (!reason.trim()) {
      autoError("Informe o motivo do cancelamento.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/sales/${sale.sale_id}/cancel`, { reason: reason.trim() });
      autoSuccess("Venda cancelada. Itens estornados ao estoque.");
      onCanceled();
      onClose();
    } catch (error) {
      handleHttpError(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid var(--border-strong)",
            p: 3,
            borderRadius: 2,
            boxShadow: 3,
            width: 400
          }}
        >
          <h2>Cancelar venda</h2>
          {sale && (
            <Typography sx={{ color: "var(--text-muted)", mb: 1 }}>
              Venda #{sale.sale_number} —{" "}
              {Number(sale.total).toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL"
              })}
            </Typography>
          )}
          <Typography sx={{ color: "var(--text-muted)", fontSize: 13 }}>
            Os produtos desta venda serão estornados ao estoque e o
            lançamento financeiro gerado por ela será cancelado.
          </Typography>

          <TextField
            fullWidth
            label="Motivo do cancelamento"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            margin="normal"
            multiline
            minRows={2}
            required
          />

          <Box sx={{ mt: 3, textAlign: "right" }}>
            <Button onClick={onClose} sx={{ mr: 1 }} disabled={submitting}>
              Voltar
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleSubmit}
              disabled={submitting}
            >
              Confirmar cancelamento
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default SaleCancelModal;
